import * as THREE from 'three';
import { WORLD_PER_HEX } from './coords.ts';

/**
 * The terraced land's detail, painted in its shader instead of in the geometry: one hex is six flat triangles of one
 * colour, which up close read as a low-resolution mosaic. Here the ground takes patches that run across the cells,
 * grain at the scale of a pebble, banded rock down the cliffs and a fine grout line round every cell, worked out per
 * pixel from the land's own coordinates. Nothing is added to the CPU or the draw calls (the slow phones' bottleneck,
 * docs/RENDIMIENTO.md): two reads of one small tiling texture made once, and some arithmetic on the GPU.
 */

const N = 128;
let noiseTex: THREE.DataTexture | null = null;

/** Tiling value noise, a different period per channel (r coarse to a fine), built once and shared. */
const landNoise = (): THREE.DataTexture => {
  if (noiseTex) return noiseTex;
  let s = 0x5eed1e5;
  const rnd = (): number => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  const data = new Uint8Array(N * N * 4);
  [4, 8, 16, 32].forEach((p, ch) => {
    const g = Array.from({ length: p * p }, rnd);
    const at = (i: number, j: number): number => g[(j % p) * p + (i % p)] as number;
    for (let y = 0; y < N; y++) {
      for (let x = 0; x < N; x++) {
        const fx = (x / N) * p;
        const fy = (y / N) * p;
        const ix = Math.floor(fx);
        const iy = Math.floor(fy);
        const u = fx - ix;
        const v = fy - iy;
        const su = u * u * (3 - 2 * u);
        const sv = v * v * (3 - 2 * v);
        const a = at(ix, iy) + (at(ix + 1, iy) - at(ix, iy)) * su;
        const b = at(ix, iy + 1) + (at(ix + 1, iy + 1) - at(ix, iy + 1)) * su;
        data[(y * N + x) * 4 + ch] = Math.round((a + (b - a) * sv) * 255);
      }
    }
  });
  noiseTex = new THREE.DataTexture(data, N, N, THREE.RGBAFormat);
  noiseTex.wrapS = noiseTex.wrapT = THREE.RepeatWrapping;
  noiseTex.magFilter = THREE.LinearFilter;
  noiseTex.minFilter = THREE.LinearMipmapLinearFilter;
  noiseTex.generateMipmaps = true;
  noiseTex.needsUpdate = true;
  return noiseTex;
};

const VERT_HEAD = 'varying vec3 vLandP;\n';
const VERT_BODY = '#include <begin_vertex>\n  vLandP = position;';

const FRAG_HEAD = /* glsl */ `
varying vec3 vLandP;
uniform sampler2D uLandNoise;
float landTop;
`;

/** After the vertex colour: patches, grain, cliff bands and the grout line. */
const FRAG_COLOR = /* glsl */ `
#include <color_fragment>
{
  vec3 fn = normalize(cross(dFdx(vLandP), dFdy(vLandP)));
  landTop = smoothstep(0.55, 0.8, abs(fn.y));
  vec2 p = vLandP.xz;
  // Two reads: each channel of the texture has its own period, so one read gives two scales.
  vec2 big = texture2D(uLandNoise, p * 0.05).rg;
  float macro = big.r;
  float pat = big.g;
  vec2 small = texture2D(uLandNoise, p * 0.33).ba;
  float grain = small.x;
#ifdef LAND_LOW
  float fine = 0.5;
#else
  float fine = smoothstep(0.3, 0.7, small.y);
#endif
  vec3 c = diffuseColor.rgb;
  // Grass (green over red and blue) dries into heath in patches and goes darker with moss in hollows.
  float grass = clamp((c.g - max(c.r, c.b)) * 30.0, 0.0, 1.0);
  float dry = smoothstep(0.45, 0.75, pat) * grass;
  c = mix(c, c * vec3(1.16, 1.06, 0.84), dry * 0.75);
  c = mix(c, c * vec3(0.82, 0.95, 0.8), smoothstep(0.5, 0.25, macro) * grass * 0.6);
  float tone = (macro - 0.5) * 0.3 + (pat - 0.5) * 0.22 + (grain - 0.5) * 0.3 + (fine - 0.5) * 0.2;
  // Down a cliff: rock in bands along the height, broken by the noise.
  float bands = 0.5 + 0.5 * sin(vLandP.y * 26.0 + pat * 7.0 + grain * 3.0);
  float side = (bands - 0.5) * 0.22 + (fine - 0.5) * 0.18;
  c *= 1.0 + mix(side, tone, landTop);
  // The grout line round each cell (pointy hexes, centres on the lattice of hexToWorld), anti-aliased.
  const float S = ${WORLD_PER_HEX.toFixed(4)};
  float fr = p.y / (S * 0.8660254);
  float fq = p.x / S - fr * 0.5;
  vec3 cube = vec3(fq, fr, -fq - fr);
  vec3 rc = floor(cube + 0.5);
  vec3 dc = abs(rc - cube);
  if (dc.x > dc.y && dc.x > dc.z) rc.x = -rc.y - rc.z;
  else if (dc.y > dc.z) rc.y = -rc.x - rc.z;
  vec2 d = p - vec2(S * (rc.x + rc.y * 0.5), S * 0.8660254 * rc.y);
  float m = max(abs(d.x), max(abs(dot(d, vec2(0.5, 0.8660254))), abs(dot(d, vec2(-0.5, 0.8660254)))));
  float e = S * 0.5 - m;
  float aa = fwidth(e);
  float w = max(0.012, aa * 0.6);
  float grout = 1.0 - smoothstep(w - aa, w + aa, e);
  float rim = 1.0 - smoothstep(0.0, S * 0.14, e);
  c *= 1.0 - landTop * (rim * 0.08 + grout * 0.26);
  diffuseColor.rgb = c;
}
`;

/** The tops' facets lit a third as hard: a lean between terraces reads as a slope, not as six shards. */
const FRAG_NORMAL = /* glsl */ `
#include <normal_fragment_begin>
  normal = normalize(mix(normal, normalize((viewMatrix * vec4(0.0, 1.0, 0.0, 0.0)).xyz), landTop * 0.7));
`;

export const landMaterial = (low: boolean): THREE.MeshStandardMaterial => {
  const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.96, metalness: 0, flatShading: true });
  const tex = landNoise();
  if (low) mat.defines = { LAND_LOW: '' };
  mat.onBeforeCompile = (sh) => {
    sh.uniforms.uLandNoise = { value: tex };
    sh.vertexShader = VERT_HEAD + sh.vertexShader.replace('#include <begin_vertex>', VERT_BODY);
    sh.fragmentShader = FRAG_HEAD + sh.fragmentShader.replace('#include <color_fragment>', FRAG_COLOR).replace('#include <normal_fragment_begin>', FRAG_NORMAL);
  };
  mat.customProgramCacheKey = () => (low ? 'somnhold-land-low' : 'somnhold-land');
  return mat;
};
